import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Button } from "src/components";
import { useToast } from "src/hooks/useToast";
import { useEventStatus } from "src/hooks/useEventStatus";
import { getCounterPoRejectCategories, reEditCounterPoRejection, rejectCounterPo } from "src/services/order-api";

// PO-REJECTION-RESTRUCTURE ==================================================

interface RejectionCategory {
  rejection_po_category_id: number;
  rejection_code: string;
  rejection_header: string;
  rejection_remark: string;
  is_review_required: boolean;
}

const PoRejectModal = ({
  stockistPoAttachmentId,
  companyCode,
  onCloseModal,
  onSuccess,
  // "re-edit" reuses this UI to change the rejection reason on an already-rejected
  // PO — submits to the RE_EDIT_ACTION event instead of REJECT.
  mode = "reject",
  existingRejectionCode,
  existingRemarks,
}: {
  stockistPoAttachmentId: number | string;
  companyCode?: number | string;
  onCloseModal: () => void;
  onSuccess?: () => void;
  mode?: "reject" | "re-edit";
  existingRejectionCode?: string;
  existingRemarks?: string;
}) => {
  const { toast } = useToast();
  const { withEventCheck } = useEventStatus();

  const [grouped, setGrouped] = useState<Record<string, RejectionCategory[]>>({});
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<RejectionCategory | null>(null);
  const [remarks, setRemarks] = useState(existingRemarks || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setIsLoadingCategories(true);
      try {
        const res = await getCounterPoRejectCategories(companyCode);
        setGrouped(res?.data?.data?.grouped ?? {});
      } catch {
        toast({ description: "Failed to load rejection categories", variant: "destructive" });
      } finally {
        setIsLoadingCategories(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-edit mode: once categories load, auto-select the category matching the
  // PO's current rejection_code so the reviewer is editing forward from there
  // instead of starting blank. Runs at most once.
  const hasSeededReEditRef = useRef(false);
  useEffect(() => {
    if (mode !== "re-edit" || hasSeededReEditRef.current || !existingRejectionCode) return;
    const all = Object.entries(grouped);
    for (const [header, categories] of all) {
      const match = categories.find((c) => c.rejection_code === existingRejectionCode);
      if (match) {
        hasSeededReEditRef.current = true;
        setSelectedHeader(header);
        setSelectedCategory(match);
        break;
      }
    }
  }, [mode, grouped, existingRejectionCode]);

  const headerOptions = useMemo(
    () => Object.keys(grouped).map((header) => ({ rejection_header: header })),
    [grouped],
  );

  const codeOptions = useMemo(() => {
    if (!selectedHeader) return [];
    return (grouped[selectedHeader] || []).map((c) => ({
      ...c,
      display_label: c.rejection_remark ? `${c.rejection_code} - ${c.rejection_remark}` : c.rejection_code,
    }));
  }, [grouped, selectedHeader]);

  const handleHeaderSelect = (value: any) => {
    const header = value?.rejection_header ?? null;
    if (header === selectedHeader) return;
    setSelectedHeader(header);
    setSelectedCategory(null);
  };

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast({ description: "Please select a rejection reason", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        event_type: mode === "re-edit" ? "COUNTER_PO_REJECTION_RE_EDIT_ACTION" : "COUNTER_PO_REJECT",
        primary_key: stockistPoAttachmentId,
        data: {
          stockist_po_attachment_id: stockistPoAttachmentId,
          rejection_code: selectedCategory.rejection_code,
          remarks: remarks.trim() || undefined,
        },
      };
      const res = mode === "re-edit"
        ? await reEditCounterPoRejection(payload, companyCode)
        : await rejectCounterPo(payload, companyCode);
      if (res?.status !== 200 && res?.status !== 201) {
        throw new Error(res?.data?.message || `Failed to ${mode === "re-edit" ? "update" : "reject"} PO`);
      }
      // These endpoints publish an event rather than writing synchronously — block
      // on the event actually being consumed before treating this as done, same
      // pattern as InvoiceOnHold/InvoiceReassign/etc.
      const streamId = res?.data?.data?.eventStreamCreated?.streamId;
      await withEventCheck(() => {}, streamId, Number(companyCode));
      toast({
        description: mode === "re-edit"
          ? "Rejection updated successfully"
          : selectedCategory.is_review_required
            ? "PO sent for rejection review"
            : "PO rejected successfully",
        variant: "default",
      });
      onSuccess?.();
      onCloseModal();
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || error?.message || `Failed to ${mode === "re-edit" ? "update" : "reject"} PO`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-y-3 w-[480px] mx-auto bg-white p-5 border border-gray-300 shadow-lg rounded-lg">
      <div>
        <p className="text-base font-semibold text-[#1B1A1F]">{mode === "re-edit" ? "Re-edit Rejection" : "Reject PO"}</p>
        <p className="text-xs text-[#666666]">
          {mode === "re-edit" ? "Pick a new reason for this PO's rejection" : "Pick a reason to reject this purchase order"}
        </p>
      </div>

      <div>
        <p className="text-sm text-[#333] mb-1">Rejection Header</p>
        <Autocomplete
          key={`header-${selectedHeader || "empty"}`}
          inputClassName="w-full"
          placeholder={isLoadingCategories ? "Loading..." : "Select a rejection header"}
          suggestionList={headerOptions}
          displayKey="rejection_header"
          uniqueKey="rejection_header"
          defaultValue={selectedHeader ? { rejection_header: selectedHeader } : undefined}
          onSelectionChange={handleHeaderSelect}
        />
      </div>

      {selectedHeader && (
        <div>
          <p className="text-sm text-[#333] mb-1">Rejection Reason</p>
          <Autocomplete
            key={`code-${selectedCategory?.rejection_code || "empty"}`}
            inputClassName="w-full"
            placeholder="Select a rejection reason"
            suggestionList={codeOptions}
            displayKey="display_label"
            uniqueKey="rejection_code"
            defaultValue={
              selectedCategory
                ? { ...selectedCategory, display_label: `${selectedCategory.rejection_code} - ${selectedCategory.rejection_remark}` }
                : undefined
            }
            onSelectionChange={(value: any) => setSelectedCategory(value ?? null)}
          />
        </div>
      )}

      {selectedCategory?.is_review_required && (
        <div className="bg-[#FFF1E3] border border-[#F5D9AE] rounded-md p-3 text-xs text-[#5E4200]">
          This rejection reason requires reviewer approval — the PO will be held pending review before it's marked rejected.
        </div>
      )}

      <div>
        <p className="text-sm text-[#333] mb-1">Remarks (optional)</p>
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Add any additional remarks"
          rows={3}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-[#f7f7f7] focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
        />
      </div>

      <div className="flex gap-2 mt-1">
        <Button className="flex-1 bg-gray-100 text-[#333] hover:bg-gray-200" onClick={onCloseModal}>
          Cancel
        </Button>
        <Button
          disabled={!selectedCategory || isSubmitting}
          isLoading={isSubmitting}
          loaderSize="small"
          className="flex-1 text-white bg-[#FF4444] hover:bg-red-700"
          onClick={handleSubmit}
        >
          {mode === "re-edit" ? "Update" : "Confirm Rejection"}
        </Button>
      </div>
    </div>
  );
};

export default PoRejectModal;
// PO-REJECTION-RESTRUCTURE ==================================================
